<template>
  <section class="edges-section">
    <div class="units-label">Edges</div>
    <div class="grid grid-6">
      <div class="metric-card edges-card">
        <div class="metric-card-label">Open PnL</div>
        <div class="metric-card-value" :class="pnlClass(edges.pnl)">{{ formatPnl(edges.pnl) }}</div>
        <div class="edges-line">
          <span>PnL Long</span>
          <strong :class="pnlClass(edges.longPnl)">{{ formatPnl(edges.longPnl) }}</strong>
        </div>
        <div class="edges-line">
          <span>PnL Short</span>
          <strong :class="pnlClass(edges.shortPnl)">{{ formatPnl(edges.shortPnl) }}</strong>
        </div>
        <div class="edges-line">
          <span>Delta</span>
          <strong>{{ formatDelta(edges.delta) }}</strong>
        </div>
        <div class="edges-line">
          <span>Recovery Price</span>
          <strong>{{ formatUsd(edges.recoveryPrice) }}</strong>
        </div>
        <div class="edges-line">
          <span>PMP Short − Long</span>
          <strong :class="pnlClass(edges.pmpSpread)">{{ formatPnl(edges.pmpSpread) }}</strong>
        </div>
      </div>
      <div class="metric-card edges-card">
        <div class="metric-card-label">Directional Bias</div>
        <div class="metric-card-value" :class="biasClass">{{ formatBias(edges.directionalBias) }}</div>
        <div class="metric-card-secondary">L {{ formatQty(edges.longQty) }} · S {{ formatQty(edges.shortQty) }}</div>
        <div class="edges-line">
          <span>BullEdge</span>
          <strong :class="pnlClass(edges.bullEdge)">{{ formatPnl(edges.bullEdge) }}</strong>
        </div>
        <div class="metric-card-secondary">{{ formatScenario(edges.currentPrice, edges.bullPrice) }}</div>
        <div class="edges-line">
          <span>BearEdge</span>
          <strong :class="pnlClass(edges.bearEdge)">{{ formatPnl(edges.bearEdge) }}</strong>
        </div>
        <div class="metric-card-secondary">{{ formatScenario(edges.currentPrice, edges.bearPrice) }}</div>
      </div>
      <div class="metric-card edges-card">
        <div class="metric-card-label">Locked Basis PnL</div>
        <div class="metric-card-value" :class="pnlClass(edges.lockedBasisPnl)">{{ formatPnl(edges.lockedBasisPnl) }}</div>
        <div class="metric-card-secondary">Qtd Pareada {{ formatQty(edges.pairedQty) }}</div>
        <div class="edges-line">
          <span>PMP Long</span>
          <strong>{{ formatUsd(edges.pmpLong) }}</strong>
        </div>
        <div class="edges-line">
          <span>PMP Short</span>
          <strong>{{ formatUsd(edges.pmpShort) }}</strong>
        </div>
      </div>
      <div class="metric-card edges-card">
        <div class="metric-card-label">Realized Today</div>
        <div class="metric-card-value" :class="pnlClass(todayProfit.realizedPnl)">{{ formatRealized(todayProfit.realizedPnl) }}</div>
        <div class="edges-line">
          <span>Close Long</span>
          <strong>{{ todayProfit.closeLongCount }}</strong>
        </div>
        <div class="edges-line">
          <span>Close Short</span>
          <strong>{{ todayProfit.closeShortCount }}</strong>
        </div>
      </div>
      <div class="metric-card edges-card">
        <div class="metric-card-label">Path P&L</div>
        <div class="path-input-row">
          <span>Alta</span>
          <input
            class="path-input"
            type="number"
            min="0"
            step="0.01"
            :value="formatTargetInput(path.bullTargetPrice)"
            @change="onPathTargetChange('bull', $event)"
          />
        </div>
        <div class="edges-line">
          <span>Total</span>
          <strong :class="pnlClass(path.bullPathPnl)">{{ formatPnl(path.bullPathPnl) }} (real. {{ formatPnl(path.bullRealizedPnl) }})</strong>
        </div>
        <div class="metric-card-secondary">Δ vs agora {{ formatPnl(path.bullPnlChange) }} · net {{ formatDelta(path.bullEndDelta) }} · fechou {{ formatQty(path.bullClosedQty) }}</div>
        <div class="path-input-row">
          <span>Baixa</span>
          <input
            class="path-input"
            type="number"
            min="0"
            step="0.01"
            :value="formatTargetInput(path.bearTargetPrice)"
            @change="onPathTargetChange('bear', $event)"
          />
        </div>
        <div class="edges-line">
          <span>Total</span>
          <strong :class="pnlClass(path.bearPathPnl)">{{ formatPnl(path.bearPathPnl) }} (real. {{ formatPnl(path.bearRealizedPnl) }})</strong>
        </div>
        <div class="metric-card-secondary">Δ vs agora {{ formatPnl(path.bearPnlChange) }} · net {{ formatDelta(path.bearEndDelta) }} · fechou {{ formatQty(path.bearClosedQty) }}</div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { computeTodayProfit } from '@/utils/computeTodayProfit.js'

export default {
  name: 'EdgesCards',
  computed: {
    ...mapGetters(['directionalEdges', 'pathPnl']),
    ...mapState({ eventLogs: (state) => state.eventLogs || [] }),
    edges() {
      return this.directionalEdges
    },
    todayProfit() {
      return computeTodayProfit(this.eventLogs)
    },
    path() {
      return this.pathPnl
    },
    biasClass() {
      const bias = this.edges.directionalBias
      if (bias == null || bias === 0) return ''
      return bias > 0 ? 'profit-positive' : 'profit-negative'
    },
  },
  methods: {
    formatTargetInput(value) {
      const n = Number(value || 0)
      return n > 0 ? n.toFixed(2) : ''
    },
    onPathTargetChange(side, event) {
      this.$store.commit('setPathScenarioPrice', { side, price: event?.target?.value })
    },
    formatBias(value) {
      if (value == null || !Number.isFinite(value)) return '—'
      const sign = value > 0 ? '+' : ''
      return sign + value.toFixed(3)
    },
    formatQty(value) {
      return Number(value || 0).toFixed(4)
    },
    formatDelta(value) {
      const n = Number(value) || 0
      const sign = n > 0 ? '+' : ''
      return sign + n.toFixed(4)
    },
    formatUsd(value) {
      if (!Number.isFinite(value) || value <= 0) return '—'
      return '$' + Math.round(value).toLocaleString('en-US')
    },
    formatScenario(referencePrice, shockPrice) {
      return `ref ${this.formatUsd(referencePrice)} · choque ${this.formatUsd(shockPrice)}`
    },
    formatPnl(value) {
      if (!Number.isFinite(value)) return '—'
      const n = Math.round(value)
      const sign = n > 0 ? '+' : ''
      return `${sign}$${n.toLocaleString('en-US')}`
    },
    formatRealized(value) {
      const n = Number(value) || 0
      const sign = n > 0 ? '+' : n < 0 ? '-' : ''
      const abs = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      return `${sign}$${abs}`
    },
    pnlClass(value) {
      if (!Number.isFinite(value) || value === 0) return ''
      return value > 0 ? 'profit-positive' : 'profit-negative'
    },
  },
}
</script>

<style scoped>
.edges-section {
  margin-bottom: 20px;
}

.edges-card {
  height: auto;
  min-height: 148px;
}

.edges-line {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
  font-size: 12px;
}

.edges-line span {
  opacity: 0.85;
}

.path-input-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  font-size: 12px;
}

.path-input {
  width: 120px;
  border: 1px solid var(--table-row-border, #3a3a3a);
  border-radius: 6px;
  background: transparent;
  color: var(--text-color);
  padding: 3px 6px;
}
</style>
